import { useMemo, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useAppSelector } from './redux';
import { useThunkDispatch } from './redux/use-thunk-dispatch';
import * as userSelector from '../../modules/core/selectors/logged-user';
import selectNetwork from '../../modules/network/actions/select-network';
import Storage from '../services/storage';

/**
 * Hook to get the current network ID.
 *
 * Priority order:
 * 1. URL params (for backward compatibility with /networks/:networkId routes)
 * 2. Redux state (currently selected network)
 * 3. Cookie (last shown network)
 * 4. LocalStorage (currently selected network)
 * 5. First available network from user's networks
 *
 * @param options - Configuration options
 * @param options.ensureSelected - If true, ensures the network is selected in Redux (default: true)
 * @returns The network ID or null if no network is available
 */
export function useNetworkId(options: { ensureSelected?: boolean } = {}) {
  const { ensureSelected = true } = options;
  const match = useRouteMatch<{ networkId?: string }>();
  const dispatch = useThunkDispatch();

  const selectedNetworkId = useAppSelector((state) => state.network.selected);
  const networks = useAppSelector(userSelector.networks);

  const networkId = useMemo(() => {
    // 1. Check URL params first (for backward compatibility)
    if (match?.params?.networkId) {
      return match.params.networkId;
    }

    // 2. Check Redux state
    if (selectedNetworkId) {
      return selectedNetworkId;
    }

    // 3. Check cookie
    const cookieNetworkId = Storage.getCookie('network_id');
    if (cookieNetworkId) {
      return cookieNetworkId;
    }

    // 4. check localStorage
    const localStorageNetworkId = Storage.getObject('currentNetwork')?.id;
    if (localStorageNetworkId) {
      return localStorageNetworkId;
    }

    // 5. Get first available network
    if (networks && networks.length > 0) {
      return networks[0].id;
    }

    return null;
  }, [match?.params?.networkId, selectedNetworkId, networks]);

  // Ensure network is selected if needed
  useEffect(() => {
    if (ensureSelected && networkId && networkId !== selectedNetworkId) {
      // Select the network without redirecting
      dispatch(selectNetwork(networkId, false, false));
    }
  }, [ensureSelected, networkId, selectedNetworkId, dispatch]);

  return networkId;
}

